import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Container, SectionHeader, CodeTag } from "@/components/Section";
import { Reveal, LineReveal } from "@/components/motion";
import Seo from "@/components/Seo";
import { fetchServices } from "@/lib/api";

const PROCESS = [
  ["01", "Pre-Construction", "Estimating, constructability, schedule, risk register and procurement strategy."],
  ["02", "Mobilization", "Site setup, environmental controls, traffic management, survey controls in place."],
  ["03", "Execution", "Self-performed earthworks, structures and concrete with daily production tracking."],
  ["04", "Commissioning", "Quality assurance, punch list, owner walk-through and warranty transition."],
];

const SPECIALIZATIONS = [
  "Storm Works",
  "Sanitary Works",
  "Water Works",
  "Paving",
  "Concrete Flatwork",
  "Sidewalks",
  "Curb & Gutter",
  "Parking Lot Construction",
  "Multiuse Pathway Construction",
  "Lockblock Walls",
  "Allanblock Walls",
  "MSE Walls",
  "Intersection Improvements",
  "Lot Improvements",
  "Lot Grading",
  "Excavation",
  "Tree Clearing",
  "Traffic Control",
  "Electrical Works",
];

export default function Services() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    fetchServices().then(setServices).catch(() => {});
  }, []);

  return (
    <div data-testid="services-page">
      <Seo
        title="Heavy Civil Services | Storm, Sanitary & Water Works, Paving, Concrete — APRASIM"
        description="Self-performed heavy civil work in Chilliwack and the Fraser Valley: storm, sanitary and water works, paving, concrete flatwork, sidewalks, curb and gutter, parking lots, retaining walls, excavation and traffic control."
        path="/services"
      />
      {/* HERO */}
      <section className="border-b-2 border-black relative overflow-hidden">
        <div className="grid-bg absolute inset-0 pointer-events-none" />
        <Container className="relative py-16 sm:py-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <Reveal y={20}>
              <CodeTag>CAPABILITIES / FULL LIST</CodeTag>
            </Reveal>
            <LineReveal
              className="mt-6 font-display text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.92]"
              delay={0.15}
              lines={[<>Heavy civil.</>, <><span className="text-[#FF4F00]">End-to-end</span></>]}
            />
            <Reveal delay={0.4}>
              <p className="mt-8 text-lg text-neutral-700 leading-relaxed max-w-2xl">
                Seven core heavy civil service lines, all self-performed. We bring the equipment, the field engineering and the crews. You bring the site plans.
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
            <Reveal delay={0.25} className="brut-border p-5 bg-black text-[#F0F0EC]">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-400">Contract Types</div>
              <div className="mt-3 font-mono text-sm space-y-1">
                <div>// Stipulated Sum</div>
                <div>// Unit Price</div>
                <div>// CM at Risk</div>
                <div>// Design-Build</div>
                <div>// Alliance / IPD</div>
              </div>
            </Reveal>
            <Reveal delay={0.35} className="brut-border p-5 bg-[#FF4F00] text-black">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase">Coverage Area</div>
              <div className="mt-3 font-display text-xl uppercase">British Columbia</div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* SERVICE LIST */}
      <section data-testid="services-list" className="border-b-2 border-black">
        <Container className="py-16 sm:py-20">
          <div className="brut-border">
            {services.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.HardHat;
              return (
                <Reveal
                  key={s.id}
                  delay={Math.min(i * 0.06, 0.3)}
                  y={30}
                  data-testid={`service-row-${s.slug}`}
                  className={`group grid grid-cols-12 gap-6 p-6 sm:p-10 hover:bg-black hover:text-[#F0F0EC] transition-colors duration-400 ${
                    i < services.length - 1 ? "border-b-2 border-black" : ""
                  }`}
                >
                  <div className="col-span-12 sm:col-span-2 flex items-start gap-3">
                    <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-500 group-hover:text-[#FF4F00] transition-colors">
                      {s.code}
                    </span>
                  </div>
                  <div className="col-span-12 sm:col-span-1">
                    <Icon className="w-8 h-8 text-[#FF4F00] group-hover:-translate-y-1 transition-transform duration-300" />
                  </div>
                  <div className="col-span-12 sm:col-span-6">
                    <h3 className="font-display text-3xl sm:text-4xl uppercase tracking-tight group-hover:translate-x-2 transition-transform duration-500">{s.title}</h3>
                    <p className="mt-3 text-base text-neutral-700 group-hover:text-neutral-300 leading-relaxed max-w-2xl transition-colors">
                      {s.description}
                    </p>
                  </div>
                  <div className="col-span-12 sm:col-span-3 flex sm:justify-end items-start">
                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.slug}`}
                      className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] link-underline"
                    >
                      Discuss scope <Icons.ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* SPECIALIZATION INDEX */}
      <section data-testid="services-specializations" className="border-b-2 border-black bg-black text-[#F0F0EC]">
        <Container className="py-20 sm:py-24">
          <Reveal>
            <div className="label-mono text-[#FF4F00] flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#FF4F00]" /> Specialization Index
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-5xl uppercase tracking-tight leading-[0.95]">
              Heavy civil, line by line
            </h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-3">
            {SPECIALIZATIONS.map((s, i) => (
              <Reveal
                key={s}
                delay={Math.min(i * 0.03, 0.4)}
                y={16}
                data-testid={`spec-tag-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="px-4 py-2 border-2 border-[#F0F0EC]/40 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors duration-300 cursor-default"
              >
                {s}
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* PROCESS */}
      <section className="border-b-2 border-black bg-[#E5E5DF]">
        <Container className="py-20 sm:py-28">
          <Reveal>
            <SectionHeader kicker="Delivery Process" title="From tender award to ribbon cutting" />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 brut-border bg-[#F0F0EC]">
            {PROCESS.map(([n, title, body], i) => (
              <Reveal
                key={n}
                delay={i * 0.1}
                className={`group p-8 hover:bg-[#FF4F00] transition-colors duration-400 ${i < 3 ? "lg:border-r-2 lg:border-black" : ""} ${
                  i % 2 !== 1 ? "md:border-r-2 md:border-black" : ""
                } ${i < 2 ? "md:border-b-2 md:border-black lg:border-b-0" : ""}`}
              >
                <div className="font-display text-6xl text-[#FF4F00] group-hover:text-black transition-colors duration-400">{n}</div>
                <h3 className="mt-4 font-display text-xl uppercase tracking-tight">{title}</h3>
                <p className="mt-3 text-sm text-neutral-700 group-hover:text-black leading-relaxed transition-colors duration-400">{body}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
